import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios"

const baseURL = `${
  process.env.REACT_APP_BASE_API_URL || ""
}/admin/rbo-pmc-migration`

// ─── Create Migration Request ───────────────────────────────────────────────

export interface MigrationRequest {
  host_pmc_id: string
  rbo_pmc_id: string
  switch_type: "RBO_PMC" | "PMC_RBO"
  note?: string
}

export interface MigrationResponse extends AxiosResponse {
  apiStatus: string
  data: number
  message: string
}

export interface MigrationResponseData extends AxiosResponse {
  data: MigrationResponse
}

export interface MigrationRequestData extends Partial<AxiosRequestConfig> {
  params: MigrationRequest
}

export function createMigrationRequest(
  { params, ...config }: MigrationRequestData,
  axiosInstance: AxiosInstance,
) {
  return axiosInstance({
    data: params,
    method: "post",
    url: `${baseURL}/request`,
    ...config,
  }) as Promise<MigrationResponseData>
}

// ─── Get Migration Requests (Your Requests and Requests to Approve list) ──────────────────────────────────────────

export interface GetMigrationRequestsParams {
  keyword?: string | null
  pageNo?: number
  noOfRecords?: number
  sortDirection?: string | null
  sortBy?: string
  type?: string
  status?: number // 0 = pending (requests to approve), 1 = submitted (your requests)
}

export enum MigrationStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

export enum SwitchType {
  RBO_PMC = "RBO_PMC",
  PMC_RBO = "PMC_RBO",
}

export type MigrationRequestItem = {
  id: number
  status: MigrationStatus
  note: string | null
  createdDt: number
  actionedAt: number | null
  host_pmc_id: string
  rbo_pmc_id: string
  switch_type: SwitchType
  creator: string | null
  actioned_by: string | null
}

export interface GetMigrationRequestsResponseDataData extends AxiosResponse {
  content: MigrationRequestItem[]
  pageable: {
    sort: {
      empty: boolean
      sorted: boolean
      unsorted: boolean
    }
    offset: number
    pageNumber: number
    pageSize: number
    paged: boolean
    unpaged: boolean
  }
  last: boolean
  totalPages: number
  totalElements: number
  number: number
  sort: {
    empty: boolean
    sorted: boolean
    unsorted: boolean
  }
  size: number
  first: boolean
  numberOfElements: number
  empty: boolean
}

export interface GetMigrationRequestsResponseData extends AxiosResponse {
  apiStatus: string
  data: GetMigrationRequestsResponseDataData
}

export interface GetMigrationRequestsResponse extends AxiosResponse {
  data: GetMigrationRequestsResponseData
}

export function getMigrationRequests(
  params: GetMigrationRequestsParams,
  axiosInstance: AxiosInstance,
) {
  return axiosInstance({
    method: "get",
    url: `${baseURL}/requests`,
    params,
  }) as Promise<GetMigrationRequestsResponse>
}

// ─── Update Migration Status (approve / reject) ──────────────────────────────

export interface UpdateMigrationStatusParams {
  request_id: number
  status: number // 0 = reject, 1 = approve
}

export interface UpdateMigrationStatusResponse extends AxiosResponse {
  data: {
    apiStatus?: string
    message?: string
    [key: string]: unknown
  }
}

export function updateMigrationStatus(
  params: UpdateMigrationStatusParams,
  axiosInstance: AxiosInstance,
) {
  return axiosInstance({
    method: "post",
    url: baseURL,
    params,
    data: "",
  }) as Promise<UpdateMigrationStatusResponse>
}
